import { z } from 'zod';
import { commissioningThresholdValiditySchema } from './commissioning-threshold-economics';

/** A period indicator selects a band; it is never another event contribution. */
export const commissioningThresholdCountModeSchema = z.enum(['event_contribution', 'period_measure']);
export const commissioningThresholdPaymentUnitSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('eventi') }).strict(),
  z.object({ kind: z.literal('valenza') }).strict(),
  z.object({ kind: z.literal('variabile'), variableCode: z.string().regex(/^[a-z][a-z0-9_]*$/) }).strict(),
]);
export type CommissioningThresholdPaymentUnit = z.infer<typeof commissioningThresholdPaymentUnitSchema>;

export const commissioningThresholdMeasureSchema = z.object({
  value: z.number().finite().nullable(),
  validity: commissioningThresholdValiditySchema,
  history: z.enum(['empty', 'complete', 'incomplete']),
}).strict().superRefine((value, ctx) => {
  if ((value.validity.status === 'valid') !== (value.value !== null)) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['value'],
    message: 'Una misura valida ha un valore; una misura non valutabile non è zero',
  });
});
export type CommissioningThresholdMeasure = z.infer<typeof commissioningThresholdMeasureSchema>;

export const commissioningThresholdMeasurementSchema = z.object({
  before: commissioningThresholdMeasureSchema,
  after: commissioningThresholdMeasureSchema,
  period: commissioningThresholdMeasureSchema,
  unit: z.enum(['events', 'valenza', 'number', 'currency', 'ratio_0_1', 'target_percent_0_100']),
  /** Rights reconstructed over every earlier ordered prefix, not the last band. */
  previouslyAwardedBandIds: z.array(z.string()),
  flatHistoryValid: z.boolean(),
}).strict();
export type CommissioningThresholdMeasurement = z.infer<typeof commissioningThresholdMeasurementSchema>;

/** Prepared by the existing event pipeline, before threshold money or bonus/malus. */
export const commissioningThresholdFactSchema = z.object({
  eventId: z.string().uuid(),
  occurredAt: z.string(),
  countOutcome: z.enum(['match', 'no_match', 'not_evaluable']),
  countValidity: commissioningThresholdValiditySchema.optional(),
  valenza: z.number().finite().nullable(),
  lineTotal: z.number().finite().nullable(),
  variables: z.record(z.union([z.string(), z.number().finite(), z.boolean(), z.null()])),
  configurationRevision: z.string(),
  listSnapshots: z.record(z.string()).optional(),
}).strict().superRefine((fact, ctx) => {
  if (fact.countValidity && (fact.countOutcome !== 'not_evaluable' || fact.countValidity.status !== 'not_evaluable')) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['countValidity'], message: 'Il motivo CONTA richiede un esito non valutabile' });
  }
});
export type CommissioningThresholdFact = z.infer<typeof commissioningThresholdFactSchema>;
